import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Fingerprint from "~icons/ph/fingerprint";
import {
  useAddPasskey,
  useCreateRecovery,
  useSubmitting,
} from "../data/mutations";
import { useShell } from "../app/shell";
import { t } from "../i18n";
import { notify } from "../ui/toast";
import { RecoveryReminder, useRecoveryReminder } from "../links/guest-account";
import type { AuthenticatedSnapshot } from "../../shared/types";

// Saving starts from reminders on other screens, and the profile shows the
// result, so this lives above both. The code stays only in this state.
export function useAccountRecovery(
  account: AuthenticatedSnapshot | null,
  hasFriends: boolean,
) {
  const me = account?.me;
  // Whether this account can be reached again after this session ends.
  const canReturn = !!me?.has_recovery || !!account?.passkeys.length;
  const reminder = useRecoveryReminder(me?.id, canReturn, hasFriends);
  const create = useCreateRecovery();
  const add = useAddPasskey();
  const submitting = useSubmitting();
  const [code, setCode] = useState(""),
    [error, setError] = useState("");
  const currentAccount = useRef(me?.id);
  useLayoutEffect(() => {
    currentAccount.current = me?.id;
  }, [me?.id]);
  async function save() {
    if (submitting) return;
    setError("");
    const accountId = me?.id;
    try {
      const data = await create.mutateAsync();
      if (currentAccount.current !== accountId) return;
      setCode(data.code);
      reminder.dismiss();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      create.reset();
    }
  }
  async function addPasskey() {
    if (submitting) return;
    setError("");
    try {
      if (await add.mutateAsync()) notify(t("أُضيف مفتاح المرور"), "success");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      add.reset();
    }
  }
  // The best way back this browser can make.
  const protect = () => (browserSupportsWebAuthn() ? addPasskey() : save());
  const clear = useCallback(() => {
    setCode("");
    setError("");
  }, []);
  return { canReturn, code, error, save, addPasskey, protect, clear, reminder };
}
export type AccountRecovery = ReturnType<typeof useAccountRecovery>;

// A prompt's buttons go with it, so focus must not fall out of the page.
export function useKeepFocusWhenGone(shown: boolean) {
  const wasShown = useRef(shown);
  useEffect(() => {
    if (wasShown.current && !shown)
      document.getElementById("main-content")?.focus();
    wasShown.current = shown;
  }, [shown]);
}

// A passkey in one touch, from the line the account's name is on, so it costs
// the page no height. The fuller reminder takes over while it is showing.
export function HomePasskey() {
  const { recovery } = useShell();
  const submitting = useSubmitting();
  const shown =
    browserSupportsWebAuthn() &&
    !recovery.canReturn &&
    !recovery.reminder.visible;
  useKeepFocusWhenGone(shown);
  const { error, clear } = recovery;
  useEffect(() => {
    if (!shown || !error) return;
    notify(error);
    clear();
  }, [shown, error, clear]);
  if (!shown) return null;
  return (
    <button
      className="home-passkey"
      aria-label={t("إضافة مفتاح مرور")}
      title={t("إذا فقدت هذا المتصفح فقدت هذا الحساب. أضف طريقة للعودة إليه.")}
      disabled={submitting}
      onClick={() => void recovery.addPasskey()}
    >
      <Fingerprint width={20} height={20} />
      <span>{t("إضافة مفتاح مرور")}</span>
    </button>
  );
}

export function AccountReminder() {
  const { recovery, openScreen } = useShell();
  const submitting = useSubmitting();
  if (!recovery.reminder.visible) return null;
  return (
    <RecoveryReminder
      disabled={submitting}
      onSave={() => {
        openScreen("profile");
        void recovery.protect();
      }}
      onDismiss={recovery.reminder.dismiss}
    />
  );
}
